import React, { useCallback, useEffect, useState } from 'react'
import MUIModal from '../Modal/Modal'
import { IFile, IReport, IReportData, IRequestedChange } from '../../helpers/Interfaces'
import Tabs from '../Tabs/Tabs'
import DocumentDetails from './DocumentDetails'
import DocumentButton from '../DocumentButton/DocumentButton'
import UploadedFiles from './UploadedFiles'
import ReportView from './ReportView'
import ApiService from '../../services/ApiService'
import { sortRequestedChanges, verifyReportName } from '../../helpers/HelperFunctions'
import { createMediaDocument, downloadMedia, getDocument, removeMedia } from '../../helpers/FirebaseFunctions'
import MenuButton from '../MenuButton/MenuButton'
import ReportUploadedView from './ReportUploadedView'
import CompleteReportView from './CompleteReportView'
import DeleteView from './DeleteView'
import CompletedUploadedFiles from './CompletedUploadedFiles'

import './ViewDocumentModal.scss'

type ViewDocumentModalProps = {
    open: boolean,
    close: CallableFunction,
    selectedDocument: IReport,
    openView: (value: any) => void,
    loading: boolean,
    showAlert: any;
    setShowAlert: CallableFunction;
}

function ViewDocumentModal(props: Readonly<ViewDocumentModalProps>) {

    const apiService = new ApiService();

    const [selectedIndex, setSelectedIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [requestedChanges, setRequestedChanges] = useState([] as IRequestedChange[]);
    const [report, setReport] = useState({} as IReportData);
    const [addedFiles, setAddedFiles] = useState([] as IFile[]);

    useEffect(() => {
        getRequestedChangesById();
        getMedia();
    }, [props.selectedDocument]);

    const handleClose = (shouldUpdate: boolean) => {
        setLoading(true);
        props.close("view", shouldUpdate);
    }

    const closeOnSubmit = (shouldUpdate: boolean) => {
        props.close("view", shouldUpdate);
    }

    async function getMedia() {
        setLoading(true);
        await getDocument(props.selectedDocument?.id, setReport);
        setLoading(false);
    }

    function setCompleteReportMedia(files: IFile[]) {
        setAddedFiles(files);
    }

    const handleClickEdit = useCallback(() => {
        handleClose(false);
        props.openView("edit");
    }, []);

    const handleClickEdit2 = useCallback(() => {
        handleClose(false);
        props.openView("edit2");
    }, []);

    function getRequestedChangesById() {

        setLoading(true);

        let data = {
            documentId: props.selectedDocument.id
        }

        apiService.getRequestedChangesById(data).then(async (result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = props.showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                props.setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            let sortedRequestedChanges = sortRequestedChanges(data?.requestedChanges)

            setRequestedChanges(sortedRequestedChanges);
        }).catch((e) => {
            let newShowAlert = props.showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            props.setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    function reopenDocument() {

        setLoading(true);

        let data = {
            documentId: props.selectedDocument.id
        }

        apiService.reopenDocument(data).then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = props.showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                props.setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            let newShowAlert = props.showAlert;
            newShowAlert.isError = false;
            newShowAlert.message = "Document moved to Pending";
            newShowAlert.open = true;
            props.setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));

            setTimeout(() => {
                handleClose(true);
            }, 2000);
        }).catch(() => {
            let newShowAlert = props.showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            props.setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    async function markAsComplete() {

        setLoading(true);

        if (addedFiles.length > 0) {
            let mediaAdded = await createMediaDocument(props.selectedDocument.mediaId, addedFiles);

            if (!mediaAdded) {
                let newShowAlert = props.showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = "Unexpected Error";
                newShowAlert.open = true;
                props.setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));
                setLoading(false);
                return;
            }
        }

        const data = {
            documentId: props.selectedDocument.id
        }

        await apiService.completeDocument(data).then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = props.showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                props.setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            let newShowAlert = props.showAlert;
            newShowAlert.isError = false;
            newShowAlert.message = "Report Completed";
            newShowAlert.open = true;
            props.setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));

            handleClose(true);
        }).catch(() => {
            let newShowAlert = props.showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            props.setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    async function requestChanges(data: any, media: IFile[], removedMedia: IFile[]) {
        setLoading(true)

        let mediaAdded = await createMediaDocument(props.selectedDocument.mediaId, media);

        if (!mediaAdded) {
            let newShowAlert = props.showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            props.setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));
            setLoading(false);
            console.log("media added wrong")
            return;
        }

        for (let file of removedMedia) {
            await removeMedia(file.fileId);
            console.log("removed media successfully")
        }

        apiService.requestChanges(data).then((result) => {
            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = props.showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                props.setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            let newShowAlert = props.showAlert;
            newShowAlert.isError = false;
            newShowAlert.message = "Changes Requested";
            newShowAlert.open = true;
            props.setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));
            closeOnSubmit(true);

        }).catch(() => {
            let newShowAlert = props.showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            props.setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    const closeAlert = () => {
        let newShowAlert = props.showAlert;
        newShowAlert.open = false;
        props.setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));
    }

    const handleDownload = async (selectedItem: string) => {
        let reportName = verifyReportName({ fileName: report?.word?.fileName, reportName: props.selectedDocument?.reportName, reportType: props.selectedDocument?.reportType });
        if (selectedItem === 'PDF') {
            await downloadMedia({ fileName: report?.pdf?.fileName, downloadingName: `${reportName}.pdf` });
        } else {
            await downloadMedia({ fileName: report?.word?.fileName, downloadingName: `${reportName}.docx` });
        }
    }

    function getCompeleteTabs() {
        let tabs = ['Document Details', 'Transcribed Report', 'Uploaded Files'];
        return tabs;
    }

    function getTabs() {
        if (props.selectedDocument.status === 'Completed') {
            return (<Tabs titles={getCompeleteTabs()} selectedIndex={selectedIndex}
                onClick={setSelectedIndex} />)
        } else if (props.selectedDocument.status === 'Submitted') {
            return (<Tabs titles={['Document Details', 'Request Changes', 'Finalise Report']} selectedIndex={selectedIndex}
                onClick={setSelectedIndex} />)
        } else if (props.selectedDocument.status === 'Draft') {
            return (<Tabs titles={['Document Details', 'Uploaded Files', 'Delete']} selectedIndex={selectedIndex}
                onClick={setSelectedIndex} />)
        } else {
            return (<Tabs titles={['Document Details', 'Uploaded Files']} selectedIndex={selectedIndex}
                onClick={setSelectedIndex} />)
        }
    }

    function getReportTitle(report: IReport) {
        if (report.reportName) {
            return report.reportName;
        } else if (report.patientName && report.reportType && report.clinic) {
            return `${report.patientName} - ${report.reportType} - ${report.clinic}`
        }

        return "View Report";
    }

    async function deleteReport() {

        setLoading(true);

        let data = {
            documentId: props.selectedDocument.id
        }

        await apiService.deleteReport(data).then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                props.setShowAlert({
                    ...props.showAlert,
                    isError: true,
                    message: data?.errorMessage,
                    open: true,
                });
                return;
            }

            props.setShowAlert({
                ...props.showAlert,
                isError: false,
                message: props.selectedDocument.reportType + " Deleted",
                open: true,
            });

            handleClose(true);
        }).catch(() => {
            props.setShowAlert({
                ...props.showAlert,
                isError: true,
                message: "Unexpected Error",
                open: true,
            });
        }).finally(() => {
            setLoading(false);
        })
    }

    function getViews(stage: string) {
        if (stage === "Draft") {
            return (<>
                {selectedIndex === 0
                    ? (<DocumentDetails report={report} selectedDocument={props.selectedDocument} />)
                    : (<></>)
                }
                {selectedIndex === 1
                    ? (<UploadedFiles selectedDocument={props.selectedDocument} />)
                    : (<></>)
                }
                {selectedIndex === 2
                    ? (<DeleteView onDelete={deleteReport} loading={loading} heading={`Delete Record for ${props.selectedDocument.patientName}`} />)
                    : (<></>)
                }
            </>)
        } else if (stage === "Pending") {
            return (<>
                {selectedIndex === 0
                    ? (<DocumentDetails report={report} selectedDocument={props.selectedDocument} />)
                    : (<></>)
                }
                {selectedIndex === 1
                    ? (<UploadedFiles selectedDocument={props.selectedDocument} />)
                    : (<></>)
                }
            </>)
        } else if (stage === "Submitted") {
            return (<>
                {selectedIndex === 0
                    ? (<DocumentDetails report={report} selectedDocument={props.selectedDocument} />)
                    : (<></>)
                }
                {selectedIndex === 1
                    ? (<ReportUploadedView report={report} setShowAlert={props.setShowAlert} showAlert={props.showAlert}
                        loading={loading} selectedDocument={props.selectedDocument}
                        requestedChanges={requestedChanges} requestChanges={requestChanges} setLoading={setLoading}
                        setAddedFiles={setAddedFiles} addedFiles={addedFiles} />)
                    : (<></>)
                }
                {selectedIndex === 2
                    ? (<CompleteReportView setShowAlert={props.setShowAlert} selectedDocument={props.selectedDocument}
                        setLoading={setLoading} setAddedFiles={setCompleteReportMedia} addedFiles={addedFiles}
                        complete={markAsComplete} showAlert={props.setShowAlert} />)
                    : (<></>)
                }
            </>)
        } else if (stage === "Completed") {
            return (<>
                {selectedIndex === 0
                    ? (<DocumentDetails report={report} selectedDocument={props.selectedDocument} />)
                    : (<></>)
                }
                {selectedIndex === 1
                    ? (<ReportView report={report} loading={loading} />)
                    : (<></>)
                }
                {selectedIndex === 2
                    ? (<CompletedUploadedFiles selectedDocument={props.selectedDocument} showAlert={props.showAlert}
                        setLoading={setLoading} setShowAlert={props.setShowAlert} loading={loading} />)
                    : (<></>)
                }
                {/* {(selectedIndex === 3)
                    ? (<FinalisedReportView selectedDocument={props.selectedDocument} />)
                    : (<></>)
                } */}
            </>)
        } else {
            return (<>
                {selectedIndex === 0
                    ? (<DocumentDetails report={report} selectedDocument={props.selectedDocument} />)
                    : (<></>)
                }
            </>)
        }
    }

    return (
        <MUIModal closeAlert={closeAlert} open={props.open} close={handleClose} style={'ime-modal-card'} >
            <div className='view-document-container'>
                <div className="view-document-header">
                    <div className="view-document-header-title">
                        {getReportTitle(props.selectedDocument)}
                    </div>
                    {getTabs()}
                </div>

                {getViews(props.selectedDocument.status)}
                {/* {(selectedIndex === 3 && props.selectedDocument.status === "Completed")
                    ? (<ReOpenDocument onReOpen={reopenDocument} loading={loading} />)
                    : (<></>)
                } */}

                {selectedIndex === 0
                    ? (<div className="view-document-footer">
                        {props.selectedDocument.status !== "Completed" && props.selectedDocument.status !== "Submitted"
                            ? (<DocumentButton width={100} text={"Edit"} onClick={handleClickEdit} loading={loading} />)
                            : (<></>)
                        }
                        {props.selectedDocument.status === "Completed" || props.selectedDocument.status === "Submitted"
                            ? (<DocumentButton width={100} text={"Edit"} onClick={handleClickEdit2} loading={loading} />)
                            : (<></>)
                        }
                        {props.selectedDocument.status === "Submitted"
                            ? (<MenuButton loading={loading} title='Download' items={["PDF", "Word"]} onSelect={handleDownload} />)
                            : (<></>)
                        }
                    </div>)
                    : (<></>)
                }

                {selectedIndex === 1
                    ? (<div className="view-document-footer">
                        {props.selectedDocument.status === "Completed"
                            ? (<MenuButton loading={loading} title='Download' items={["PDF", "Word"]} onSelect={handleDownload} />)
                            : (<></>)
                        }
                    </div>)
                    : (<></>)
                }

                {selectedIndex === 2
                    ? (<div className="view-document-footer">
                        {props.selectedDocument.status === "Submitted"
                            ? (<DocumentButton width={200} text={"Complete"} onClick={markAsComplete} loading={loading} />)
                            : (<></>)
                        }
                    </div>)
                    : (<></>)
                }

                {/* {selectedIndex === 3
                    ? (<div className="view-document-footer">
                        {props.selectedDocument.status === "Completed"
                            ? (<MenuButton loading={loading} title='Download' items={getFinalisedReportDownloadItems()}
                                onSelect={handleFinalisedReportDownload} />)
                            : (<></>)
                        }
                    </div>)
                    : (<></>)
                } */}
            </div>
        </MUIModal>
    )
}

export default ViewDocumentModal